import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QuestionnaireNavSidebar } from '../components/questionnaire/QuestionnaireNavSidebar';
import { QuestionnaireTopHeader } from '../components/questionnaire/QuestionnaireTopHeader';
import { SIDEBAR_SECTIONS } from '../features/flow/sidebarSections';
import PersonalInfoTab from './settings/PersonalInfoTab';
import PaymentHistoryTab from './settings/PaymentHistoryTab';
import TaxDeclarationsTab from './settings/TaxDeclarationsTab';

type Tab = 'personal' | 'payments' | 'declarations';

function isValidTab(v: string | null): v is Tab {
  return v === 'personal' || v === 'payments' || v === 'declarations';
}

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: Tab = isValidTab(rawTab) ? rawTab : 'personal';

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'personal', label: t('page.tabs.personal') },
    { id: 'payments', label: t('page.tabs.payments') },
    { id: 'declarations', label: t('page.tabs.declarations') },
  ];

  const handleTabChange = (tab: Tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="h-screen min-h-0 flex flex-col bg-[#F9FAFB] md:bg-gray-50 overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <QuestionnaireNavSidebar
          screens={[]}
          currentScreenId={null}
          visitedScreenIds={[]}
          sections={SIDEBAR_SECTIONS}
          desktopCollapsed={desktopSidebarCollapsed}
          onDesktopCollapsedChange={setDesktopSidebarCollapsed}
          mobileDrawerOpen={mobileNavOpen}
          onMobileDrawerClose={() => setMobileNavOpen(false)}
        />
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-[#F9FAFB] md:bg-white">
          <QuestionnaireTopHeader
            title={t('page.title')}
            onSidebarToggle={() => setMobileNavOpen(true)}
          />
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-xl font-bold text-gray-900 mb-6">{t('page.heading')}</h1>

              <div className="flex gap-1 border-b border-gray-200 mb-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={[
                      'whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                      activeTab === tab.id
                        ? 'border-[#1066CB] text-[#1066CB]'
                        : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300',
                    ].join(' ')}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'personal' && <PersonalInfoTab />}
              {activeTab === 'payments' && <PaymentHistoryTab />}
              {activeTab === 'declarations' && <TaxDeclarationsTab />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
